import type { AppError, ErrorCode } from './types';

type Meta = Record<string, string | number | boolean | null>;

interface Copy {
  recoverable: boolean;
  summary: string;
  action: string;
}

/**
 * Every failure has a plain summary and one next action. Errors never apologise
 * and never blame the user. UI-013
 */
const COPY: Record<ErrorCode, Copy> = {
  INVALID_URL: {
    recoverable: true,
    summary: 'That is not a complete web address.',
    action: 'Paste a full link that starts with https://.',
  },
  UNSUPPORTED_SCHEME: {
    recoverable: true,
    summary: 'Only secure https:// links to a file are supported.',
    action: 'Paste an https:// link, or use localhost for development.',
  },
  CREDENTIALS_IN_URL: {
    recoverable: true,
    summary: 'This link contains a username or password.',
    action: 'Remove the credentials from the link. OneFlow will not store or send them.',
  },
  WEBPAGE_NOT_FILE: {
    recoverable: true,
    summary: 'This is a video site page, not a direct file.',
    action: 'OneFlow only opens direct file links. It does not extract media from streaming sites.',
  },
  CORS_OR_NETWORK: {
    recoverable: true,
    summary: 'Cannot read remote file due to CORS, or the server is unreachable.',
    action:
      'Check the link in a new tab. If the file opens there, ask the host to send Access-Control-Allow-Origin. OneFlow will not use a proxy.',
  },
  HTTP_FORBIDDEN: {
    recoverable: true,
    summary: 'The server refused access (HTTP 403).',
    action: 'The link may have expired or need a login. Paste a fresh link.',
  },
  HTTP_NOT_FOUND: {
    recoverable: true,
    summary: 'The server has no file at this address (HTTP 404).',
    action: 'Check the link for typos, or paste a new one.',
  },
  HTTP_EXPIRED: {
    recoverable: true,
    summary: 'The link has expired or was removed (HTTP 410).',
    action: 'Paste a fresh link to the same file to continue.',
  },
  HTTP_STATUS: {
    recoverable: true,
    summary: 'The server returned an unexpected response.',
    action: 'Try again. If it keeps happening, paste a different link.',
  },
  RANGE_NOT_SATISFIABLE: {
    recoverable: true,
    summary: 'The server rejected the requested part of the file (HTTP 416).',
    action: 'The file may have changed. Start the download again.',
  },
  RANGE_UNSUPPORTED: {
    recoverable: true,
    summary: 'This server sends the whole file in order and cannot skip ahead.',
    action:
      'Playback works up to the saved part. Jumping ahead unlocks once the download passes that point.',
  },
  BAD_CONTENT_RANGE: {
    recoverable: true,
    summary: 'The server sent a different part of the file than requested.',
    action:
      'Nothing was saved from that response. Retry; if it repeats, the host is misconfigured.',
  },
  CONTENT_ENCODING: {
    recoverable: false,
    summary: 'The server compresses this file in transit, which breaks partial downloads.',
    action: 'Ask the host to serve the file without Content-Encoding.',
  },
  TRUNCATED_BODY: {
    recoverable: true,
    summary: 'The connection ended before the whole part arrived.',
    action: 'Saved bytes are kept. Resume to continue from the gap.',
  },
  SOURCE_CHANGED: {
    recoverable: true,
    summary: 'The file at this link changed while it was being saved.',
    action: 'Keep the old partial copy aside or discard it, then start again.',
  },
  OPAQUE_REDIRECT: {
    recoverable: true,
    summary: 'The link redirected somewhere the browser will not let OneFlow read.',
    action: 'Open the link in a tab, copy the final address, and paste that instead.',
  },
  QUOTA_EXCEEDED: {
    recoverable: true,
    summary: 'This device has no room left for the file.',
    action: 'Free space or delete another saved file, then resume.',
  },
  STORAGE_UNAVAILABLE: {
    recoverable: false,
    summary: 'This browser cannot store files for OneFlow right now.',
    action: 'Leave private browsing, or try a current Chrome, Edge, Firefox or Safari.',
  },
  STORAGE_CORRUPT: {
    recoverable: true,
    summary: 'Some saved data could not be verified.',
    action: 'OneFlow kept the parts it could prove and will fetch the rest.',
  },
  DECODE_UNSUPPORTED: {
    recoverable: true,
    summary: 'This browser cannot play this format.',
    action: 'The download continues. Export the original file to open it in another app.',
  },
  PREVIEW_FAILED: {
    recoverable: true,
    summary: 'The preview stopped.',
    action: 'The download is unaffected. Try the preview again or export the original.',
  },
  EXPORT_FAILED: {
    recoverable: true,
    summary: 'The file could not be written to the chosen location.',
    action: 'Choose another location, or free some disk space and try again.',
  },
  WORKER_CRASHED: {
    recoverable: true,
    summary: 'A background task stopped unexpectedly.',
    action: 'Saved bytes are intact. Resume to continue.',
  },
  EMPTY_FILE: {
    recoverable: false,
    summary: 'The file is empty.',
    action: 'Nothing to preview. Paste a different link.',
  },
  ABORTED: {
    recoverable: true,
    summary: 'The download was stopped.',
    action: 'Resume to continue from what is saved.',
  },
  UNKNOWN: {
    recoverable: true,
    summary: 'Something unexpected went wrong.',
    action: 'Try again. Open details to copy a redacted report.',
  },
};

export function makeError(code: ErrorCode, meta?: Meta): AppError {
  const c = COPY[code];
  return {
    code,
    recoverable: c.recoverable,
    summary: c.summary,
    action: c.action,
    ...(meta ? { meta } : {}),
  };
}

/** Typed error carrier for code paths that must throw. */
export class OneFlowError extends Error {
  readonly appError: AppError;
  constructor(code: ErrorCode, meta?: Meta, options?: { cause?: unknown }) {
    const e = makeError(code, meta);
    super(e.summary, options);
    this.name = 'OneFlowError';
    this.appError = e;
  }
}

export function toAppError(err: unknown): AppError {
  if (err instanceof OneFlowError) return err.appError;
  if (err instanceof DOMException && err.name === 'QuotaExceededError')
    return makeError('QUOTA_EXCEEDED');
  if (err instanceof DOMException && err.name === 'AbortError') return makeError('ABORTED');
  return makeError('UNKNOWN');
}

export function isAbort(err: unknown): boolean {
  return (
    (err instanceof DOMException && err.name === 'AbortError') ||
    (err instanceof Error && err.name === 'AbortError')
  );
}
